import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchJsonWithBackendFallback, fetchWithBackendFallback } from '../../utils/backendApi';
import '../../assets/fontello-change/fontello-79be5077/css/fontello.css';
import './Social.css';

const SOCIAL_EXPERIENCES = [
  {
    key: 'music',
    eyebrow: 'Friday Energy',
    title: 'Live Music Sessions',
    description: 'A bright, social atmosphere with local performers, tribute acts, and member nights that feel lively but welcoming.',
  },
  {
    key: 'family',
    eyebrow: 'Weekend Family Time',
    title: 'Family Events & Discos',
    description: 'Safe, upbeat social evenings with music, games, and flexible layouts for mixed ages and larger family groups.',
  },
  {
    key: 'private-hire',
    eyebrow: 'Tailored Setup',
    title: 'Private Hire & Celebrations',
    description: 'From after-parties to birthdays and club meetups, we can shape the room and timings around your event style.',
  },
  {
    key: 'community',
    eyebrow: 'Community Focus',
    title: 'Community Nights',
    description: 'Quiz nights, social club meetups, and neighbourhood gatherings designed to keep local people connected year-round.',
  },
];

const CLUB_RHYTHM = [
  {
    key: 'thursday',
    day: 'Thursday',
    title: 'Quiz & Club Night',
    detail: 'Friendly competition, light food, and social tables for regular members and new visitors.',
  },
  {
    key: 'friday',
    day: 'Friday',
    title: 'Live Performance Slot',
    detail: 'Local bands and tribute sets with room for dancing, social drinks, and larger evening crowds.',
  },
  {
    key: 'weekend',
    day: 'Weekend',
    title: 'Private Celebrations',
    detail: 'Booked functions, milestone events, and family celebrations with flexible setup options.',
  },
];

const TEMP_OFFER_SLIDES = [
  {
    key: 'music',
    tone: 'aqua',
    badge: 'Live Nights',
    title: 'Live Music Sessions',
    text: 'Bands, tribute performances, and social club evenings with room to dance and connect.',
  },
  {
    key: 'family',
    tone: 'sky',
    badge: 'Family Events',
    title: 'Discos & Shared Socials',
    text: 'Family-friendly sessions with flexible room setup for mixed ages and relaxed evenings.',
  },
  {
    key: 'hire',
    tone: 'ocean',
    badge: 'Private Hire',
    title: 'Celebrations & Venue Hire',
    text: 'Book the venue for birthdays, after-parties, and milestone moments with tailored support.',
  },
  {
    key: 'community',
    tone: 'frost',
    badge: 'Community Focus',
    title: 'Club Nights & Local Meetups',
    text: 'Quiz nights and regular social events that keep the local community together all year.',
  },
];

const Social = () => {
  const [activeOfferSlide, setActiveOfferSlide] = useState(0);

  // Upcoming social events fetched from the backend
  const [socialEvents, setSocialEvents]         = useState([]);
  const [eventsLoading, setEventsLoading]       = useState(true);

  // Request-to-attend modal state
  const [requestModal, setRequestModal]         = useState(null); // { event } or null
  const [requestForm, setRequestForm]           = useState({ fullName: '', email: '', phone: '', attendees: '1', notes: '' });
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError]         = useState('');
  const [requestSuccess, setRequestSuccess]     = useState('');

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveOfferSlide((current) => (current + 1) % TEMP_OFFER_SLIDES.length);
    }, 3200);

    return () => window.clearInterval(intervalId);
  }, []);

  // Fetch events with category 'social' from the backend on mount
  useEffect(() => {
    const fetchSocialEvents = async () => {
      setEventsLoading(true);
      try {
        const data = await fetchJsonWithBackendFallback('/public/events');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcoming = (Array.isArray(data) ? data : [])
          .filter((e) => e.category === 'social' && e.event_date && new Date(e.event_date) >= today)
          .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
        setSocialEvents(upcoming);
      } catch {
        setSocialEvents([]);
      } finally {
        setEventsLoading(false);
      }
    };
    fetchSocialEvents();
  }, []);

  const openRequestModal = (event) => {
    setRequestModal({ event });
    setRequestForm({ fullName: '', email: '', phone: '', attendees: '1', notes: '' });
    setRequestSuccess('');
    setRequestError('');
  };

  const closeRequestModal = () => {
    setRequestModal(null);
    setRequestSuccess('');
    setRequestError('');
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setRequestError('');
    if (!requestForm.fullName.trim() || !requestForm.email.trim()) {
      setRequestError('Please enter your name and email address.');
      return;
    }
    setRequestSubmitting(true);
    try {
      await fetchWithBackendFallback('/bookings/event-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_title:      requestModal.event.title,
          group_name:       'Clipstone Social Club',
          full_name:        requestForm.fullName.trim(),
          email:            requestForm.email.trim(),
          phone:            requestForm.phone.trim() || null,
          attendees:        parseInt(requestForm.attendees, 10) || 1,
          preferred_date:   requestModal.event.event_date,
          notes:            requestForm.notes.trim() || null,
          booking_section:  'social',
          booking_type:     'social-event',
        }),
      });
      setRequestSuccess(`Your request to attend "${requestModal.event.title}" has been sent. We'll be in touch soon!`);
    } catch {
      setRequestError('Could not send your request right now — please try again later.');
    } finally {
      setRequestSubmitting(false);
    }
  };

  return (
    <div className="social-redesign-page">
      <section className="social-redesign-hero" id="social-hero-main">
        <div className="social-redesign-hero-copy">
          <span className="social-redesign-kicker">Clipstone Social Club</span>
          <h1>A brighter space for events, celebrations, and community nights.</h1>
          <p>
            Designed for people to gather, celebrate, and stay connected. From live evenings to private hire,
            the social club is set up to feel welcoming, flexible, and easy to book.
          </p>
          <div className="social-redesign-cta-row">
            <Link to="/social/booking" className="social-redesign-btn social-redesign-btn--solid">
              Book The Venue
            </Link>
            <Link to="/social/contact" className="social-redesign-btn social-redesign-btn--outline">
              Opening Times
            </Link>
            <Link to="/social/staff" className="social-redesign-btn social-redesign-btn--ghost">
              Meet The Team
            </Link>
          </div>
        </div>

        <div className="social-redesign-hero-panel" aria-label="Social club highlights">
          <article className="social-redesign-stat-card">
            <span>Venue Capacity</span>
            <strong>Up to 300</strong>
            <p>Flexible floor plans for lively events and structured community nights.</p>
          </article>
          <article className="social-redesign-stat-card">
            <span>Private Functions</span>
            <strong>Tailored Setup</strong>
            <p>Room layout, timing, and event support adjusted around your booking.</p>
          </article>
          <article className="social-redesign-stat-card">
            <span>Community Atmosphere</span>
            <strong>Year-Round Socials</strong>
            <p>Music, quiz nights, and family events that keep local people coming back.</p>
          </article>
        </div>
      </section>

      <main className="social-redesign-content">
        <section className="social-redesign-section social-redesign-section--temp" id="social-temp-offers">
          <header className="social-redesign-header">
            <p>Slideshow</p>
            <h2>Things We Offer</h2>
          </header>

          <div className="social-temp-slideshow" aria-label="Social offerings slideshow">
            {TEMP_OFFER_SLIDES.map((slide, index) => (
              <article
                key={slide.key}
                className={`social-temp-slide social-temp-slide--${slide.tone} ${index === activeOfferSlide ? 'is-active' : ''}`}
                aria-hidden={index !== activeOfferSlide}
              >
                <span>{slide.badge}</span>
                <h3>{slide.title}</h3>
                <p>{slide.text}</p>
              </article>
            ))}

            <div className="social-temp-dots" aria-label="Choose offer slide">
              {TEMP_OFFER_SLIDES.map((slide, index) => (
                <button
                  key={`${slide.key}-dot`}
                  type="button"
                  className={`social-temp-dot ${index === activeOfferSlide ? 'is-active' : ''}`}
                  aria-label={`Show ${slide.title}`}
                  onClick={() => setActiveOfferSlide(index)}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="social-redesign-section" id="social-experiences">
          <header className="social-redesign-header">
            <p>What We Host</p>
            <h2>Experiences Built Around The Community</h2>
          </header>

          <div className="social-redesign-grid">
            {SOCIAL_EXPERIENCES.map((item) => (
              <article className="social-redesign-card" key={item.key}>
                <span className="social-redesign-card-eyebrow">{item.eyebrow}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="social-redesign-section" id="social-rhythm">
          <header className="social-redesign-header">
            <p>Club Rhythm</p>
            <h2>A Typical Week At The Social Club</h2>
          </header>

          <div className="social-redesign-timeline">
            {CLUB_RHYTHM.map((slot) => (
              <article className="social-redesign-timeline-row" key={slot.key}>
                <strong>{slot.day}</strong>
                <div>
                  <h3>{slot.title}</h3>
                  <p>{slot.detail}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="social-redesign-section" id="social-quick-links">
          <header className="social-redesign-header">
            <p>Quick Access</p>
            <h2>Everything You Need In One Place</h2>
          </header>

          <div className="social-redesign-links">
            <Link to="/social/booking" className="social-redesign-link-card">
              <span>Venue Hire</span>
              <strong>Booking and pricing</strong>
              <p>See room capacities, pricing details, and submit your booking enquiry.</p>
            </Link>
            <Link to="/trustees" className="social-redesign-link-card">
              <span>Management</span>
              <strong>Meet the Trustees</strong>
              <p>Find the board and committee members who lead events and operations.</p>
            </Link>
            <Link to="/contact#social" className="social-redesign-link-card">
              <span>Contact Details</span>
              <strong>Opening times and info</strong>
              <p>Get in touch quickly with up-to-date 9 to 5 hours and contact options.</p>
            </Link>
          </div>
        </section>

        {/* Upcoming Social Events — populated by events with category 'social' added via the admin Event Manager */}
        <section className="social-redesign-section" id="social-upcoming-events">
          <header className="social-redesign-header">
            <p>What's On</p>
            <h2>Upcoming Social Events</h2>
          </header>

          {eventsLoading ? (
            <p className="social-events-state-text">Loading upcoming events…</p>
          ) : socialEvents.length === 0 ? (
            <p className="social-events-state-text">No upcoming social events at the moment — check back soon.</p>
          ) : (
            <div className="social-events-grid">
              {socialEvents.map((event) => (
                <article key={event.id} className="social-event-card">
                  {event.image_url && (
                    <div className="social-event-card-img">
                      <img src={event.image_url} alt={event.title} />
                    </div>
                  )}
                  <div className="social-event-card-body">
                    <span className="social-event-date">
                      {new Date(event.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                      {event.start_time ? ` · ${event.start_time}` : ''}
                    </span>
                    <h3>{event.title}</h3>
                    {event.description && <p>{event.description}</p>}
                    {event.location && (
                      <p className="social-event-location">
                        <i className="icon-location" aria-hidden="true" />
                        <span>{event.location}</span>
                      </p>
                    )}
                    <button
                      type="button"
                      className="social-redesign-btn social-redesign-btn--solid social-event-request-btn"
                      onClick={() => openRequestModal(event)}
                    >
                      Request to attend
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Request-to-attend modal */}
      {requestModal && (
        <div className="social-event-modal-overlay" role="dialog" aria-modal="true" aria-label="Request to attend event">
          <div className="social-event-modal">
            <button type="button" className="social-event-modal-close" onClick={closeRequestModal} aria-label="Close">✕</button>
            <p className="social-event-modal-kicker">Clipstone Social Club</p>
            <h3>Request to attend</h3>
            <p className="social-event-modal-subtitle">
              {requestModal.event.title}
              {requestModal.event.event_date
                ? ` · ${new Date(requestModal.event.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`
                : ''}
            </p>

            {requestSuccess ? (
              <div className="social-event-modal-success">
                <div className="social-event-modal-success-mark" aria-hidden="true">✓</div>
                <h4>Request received</h4>
                <p className="social-event-modal-success-copy">{requestSuccess}</p>
                <div className="social-event-modal-success-summary">
                  <div>
                    <span>Event</span>
                    <strong>{requestModal.event.title}</strong>
                  </div>
                  <div>
                    <span>Date</span>
                    <strong>
                      {requestModal.event.event_date
                        ? new Date(requestModal.event.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                        : 'To be confirmed'}
                    </strong>
                  </div>
                  <div>
                    <span>Attendees</span>
                    <strong>{requestForm.attendees || '1'}</strong>
                  </div>
                </div>
                <div className="social-event-modal-actions">
                  <button
                    type="button"
                    className="social-redesign-btn social-redesign-btn--solid"
                    onClick={closeRequestModal}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleRequestSubmit} className="social-event-modal-form" noValidate>
                <div className="social-event-modal-grid">
                  <label className="social-event-modal-field">
                    <span>Your name *</span>
                    <input
                      required
                      type="text"
                      value={requestForm.fullName}
                      onChange={(e) => setRequestForm((f) => ({ ...f, fullName: e.target.value }))}
                      placeholder="e.g. Jane Smith"
                    />
                  </label>
                  <label className="social-event-modal-field">
                    <span>Email *</span>
                    <input
                      required
                      type="email"
                      value={requestForm.email}
                      onChange={(e) => setRequestForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="e.g. jane@example.com"
                    />
                  </label>
                  <label className="social-event-modal-field">
                    <span>Phone</span>
                    <input
                      type="tel"
                      value={requestForm.phone}
                      onChange={(e) => setRequestForm((f) => ({ ...f, phone: e.target.value }))}
                      placeholder="Optional"
                    />
                  </label>
                  <label className="social-event-modal-field">
                    <span>Attendees</span>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={requestForm.attendees}
                      onChange={(e) => setRequestForm((f) => ({ ...f, attendees: e.target.value }))}
                    />
                  </label>
                </div>
                <label className="social-event-modal-field social-event-modal-field--full">
                  <span>Message (optional)</span>
                  <textarea
                    rows={3}
                    value={requestForm.notes}
                    onChange={(e) => setRequestForm((f) => ({ ...f, notes: e.target.value }))}
                    placeholder="Any accessibility needs or special requirements…"
                  />
                </label>
                {requestError && <p className="social-event-modal-error">{requestError}</p>}
                <div className="social-event-modal-actions">
                  <button
                    type="submit"
                    className="social-redesign-btn social-redesign-btn--solid"
                    disabled={requestSubmitting}
                  >
                    {requestSubmitting ? 'Sending…' : 'Send request'}
                  </button>
                  <button
                    type="button"
                    className="social-redesign-btn social-redesign-btn--ghost"
                    onClick={closeRequestModal}
                    disabled={requestSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Social;
